import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
    AbstractControl,
    ReactiveFormsModule,
    UntypedFormBuilder,
    Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import {
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelContent,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle
} from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { RecaptchaModule } from 'ng-recaptcha-2';
import { of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import { ClientService } from '../../../core/services';
import { Client } from '../../../domain/models';
import { PhoneInputComponent } from '../../../shared/components';
import { HelpService } from './help.service';

@Component({
    selector: 'app-help',
    templateUrl: './help.component.html',
    styleUrls: ['./help.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatCard,
        MatTab,
        MatTabLabel,
        MatExpansionPanel,
        MatExpansionPanelContent,
        MatExpansionPanelTitle,
        MatExpansionPanelHeader,
        MatFormFieldModule,
        RecaptchaModule,
        PhoneInputComponent,
        MatAccordion,
        MatTabGroup,
        MatIcon,
        TranslocoModule,
        FlexLayoutModule,
        MatButtonModule,
        MatInputModule
    ]
})
export class HelpComponent implements OnInit {
    captcha: string | undefined = undefined;
    client: Client | undefined;
    submitted = false;
    panelOpenState = false;
    requestForm = this._fb.group({
        name: ['', Validators.required],
        email: ['', [Validators.email, Validators.required]],
        phone: ['', []],
        subject: ['', Validators.required],
        description: ['', Validators.required],
        recaptcha: ['', Validators.required]
    });

    client$ = this._client.latest;

    faqs$ = this.client$.pipe(
        switchMap((client) =>
            this._client.getFaqs().pipe(
                catchError((error) => {
                    console.error('Error fetching FAQs:', error);
                    return of([]); // Retorna um array vazio em caso de erro
                })
            )
        )
    );

    constructor(
        private transService: TranslocoService,
        private helpService: HelpService,
        private _location: Location,
        private readonly _fb: UntypedFormBuilder,
        private readonly _client: ClientService,
        private _googleAnalyticsService: GoogleAnalyticsService
    ) {}

    ngOnInit(): void {
        this._googleAnalyticsService.pageView('Help and Support');
        this._client.latest.subscribe((client) => {
            this.client = client;
        });
    }

    goBack() {
        this._location.back();
    }

    onSubmit(event: any): void {
        if (this.requestForm.valid && !this.submitted) {
            this._googleAnalyticsService.click(
                'Contact Us',
                'Contact Us Submitted'
            );

            var request = this.requestForm.value;
            if (this.client?.clientId) {
                request.clientId = this.client?.clientId;
            }

            this.helpService
                .submitSupportRequest(this.requestForm.value)
                .pipe(
                    tap(() => {
                        this.postSubmit();
                    })
                )
                .subscribe();
        }
    }

    resolved(captchaResponse: string) {
        this.captcha = captchaResponse;
        this.recaptcha?.setValue(captchaResponse);
    }

    get validCaptcha(): boolean {
        return (
            this.captcha != undefined &&
            this.captcha != null &&
            this.captcha.length > 0
        );
    }

    get name(): AbstractControl | null {
        return this.requestForm.get('name');
    }
    get email(): AbstractControl | null {
        return this.requestForm.get('email');
    }
    get phone(): AbstractControl | null {
        return this.requestForm.get('phone');
    }
    get subject(): AbstractControl | null {
        return this.requestForm.get('subject');
    }
    get description(): AbstractControl | null {
        return this.requestForm.get('description');
    }

    get recaptcha(): AbstractControl | null {
        return this.requestForm.get('recaptcha');
    }

    private postSubmit() {
        this.submitted = true;
    }
}
